import { Injectable } from '@angular/core';

import { ApplicationConfig } from '@lockton-apps/shared/interfaces';
import {
  defaults,
  differenceBy,
  find,
  findIndex,
  get,
  map,
  meanBy,
  omit,
  orderBy,
  reduce,
} from 'lodash';
import { PresentationBuilderAdapter } from '../adapters/presentation-builder-adapter.service';
import {
  BlockTypes,
  Blocks,
  DocumentData,
  DocumentOptions,
  FormlyFieldConfigWithDisplayViewOptions,
  TemplateBlock,
} from '../interfaces';
import {
  PBValueData,
  PBValueDataType,
} from '../interfaces/presentation-builder-value-data.model';
import { PresentationBuilderValueAccessor } from '../value-accessors/presentation-builder-value-accessor.service';

@Injectable({
  providedIn: 'root',
})
export class DocumentBuilderService {
  constructor(
    private adapter: PresentationBuilderAdapter<
      ApplicationConfig,
      FormlyFieldConfigWithDisplayViewOptions
    >,
    private valueAccessor: PresentationBuilderValueAccessor<FormlyFieldConfigWithDisplayViewOptions>
  ) {}

  generateDisplayComponents(
    rawData: ApplicationConfig,
    model: Record<string, unknown>,
    documentData?: DocumentData,
    options?: DocumentOptions
  ): Blocks[] {
    const viewData = this.adapter.read(rawData);

    const viewDataWithValues = this.valueAccessor.getValue(
      viewData,
      model,
      documentData?.editorModel
    );

    const blocks = this.createBlocks(viewDataWithValues, options);

    if (!documentData?.blocks) {
      return map(blocks, (block, index) => ({
        ...block,
        order: index,
      })) as Blocks[];
    }

    const originalBlocks = map(documentData.blocks, (block) => {
      const configBlock = find(blocks, { id: block.id });
      if (!configBlock) {
        return defaults(
          {
            initialValue: get(documentData, `editorModel.${block.id}`),
          },
          omit(block, 'initialValue')
        ) as TemplateBlock;
      }
      switch (block.type) {
        case BlockTypes.EDITOR: {
          return defaults(
            {
              ...block,
              initialValue: get(documentData, `editorModel.${block.id}`),
            },
            configBlock
          );
        }
        case BlockTypes.DYNAMIC: {
          return defaults(omit(block, 'inputs'), configBlock);
        }
        default:
          return block;
      }
    }) as Blocks[];

    const newConfigBlocks = differenceBy(blocks, originalBlocks, 'id');

    return reduce(
      newConfigBlocks,
      (acc, cur) => {
        const previousIndex = findIndex(blocks, { id: cur.id }) - 1;
        const previousBlockId = blocks[previousIndex].id;

        const sortedBlocks = orderBy(acc, 'order');

        const index = findIndex(sortedBlocks, { id: previousBlockId });

        const betweenBlocks = [sortedBlocks[index], sortedBlocks[index + 1]];
        const newOrder = meanBy(betweenBlocks, 'order');

        return [...acc, { ...cur, order: newOrder }] as Blocks[];
      },
      originalBlocks
    );
  }

  private createBlocks(
    viewDataWithValues: PBValueData[],
    options?: DocumentOptions
  ) {
    const breakAfterSection = options?.breakAfterSection || false;

    let previousSection = '';

    return reduce(
      viewDataWithValues,
      (acc, cur) => {
        const section = cur.id.split('-')[0];

        if (
          breakAfterSection &&
          previousSection &&
          previousSection !== section
        ) {
          acc.push({
            id: `${section}-break`,
            type: BlockTypes.PAGE_BREAK,
            hidden: false,
            required: false,
          });
        }
        previousSection = section;

        switch (cur.type) {
          case PBValueDataType.TEXT: {
            return [
              ...acc,
              {
                id: cur.id,
                type: BlockTypes.EDITOR,
                initialValue: cur.value,
                required: true,
                hidden: false,
              } as TemplateBlock,
            ];
          }

          case PBValueDataType.FIELD: {
            return [
              ...acc,
              {
                id: cur.id,
                type: BlockTypes.DYNAMIC,
                component: cur.component,
                inputs: cur.inputs,
                required: true,
                hidden: false,
              },
            ];
          }

          default:
            return acc;
        }
      },
      [] as Omit<Blocks, 'order'>[]
    );
  }
}
